import { useState } from 'react'
import {
  CADENCES,
  COLLECTION_LABELS,
  createRule,
  type AmountMode,
  type CollectionMethod,
} from '@core/recurring'
import { parseAmountToCents } from '@core/money'
import { Button, Card, ErrorNote, Field } from '@design/components'
import { todayIso } from '@core/dates'
import { Overlay } from '@design/Overlay'

/**
 * Setting up a bill that comes back.
 *
 * The only genuinely important question on this form is fixed or variable,
 * and it is asked in plain language rather than as a mode switch: "always the
 * same amount" is a thing a person knows about their rent, and "amount
 * changes" is a thing they know about their electricity. Everything after it
 * follows from that answer — a variable rule cannot carry payment details at
 * all, so the fields simply are not there.
 */
export function RuleForm({
  spaceId,
  userId,
  initial,
  onDone,
  onCancel,
}: {
  spaceId: string
  userId: string
  initial?: {
    partyId?: string | null
    description?: string
    category?: string | null
    amountCents?: number
    iban?: string | null
    paymentReference?: string | null
    dueDate?: string
  }
  onDone: () => void
  onCancel: () => void
}) {
  const today = todayIso()
  const [description, setDescription] = useState(initial?.description ?? '')
  const [mode, setMode] = useState<AmountMode>('fixed')
  const [amount, setAmount] = useState(
    initial?.amountCents ? (initial.amountCents / 100).toFixed(2) : '',
  )
  const [cadence, setCadence] = useState(CADENCES[0]!.key)
  const [day, setDay] = useState(String(Number((initial?.dueDate ?? today).slice(8, 10))))
  const [endsOn, setEndsOn] = useState('')
  const [collection, setCollection] = useState<CollectionMethod>('manual')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    setBusy(true)
    setError('')
    try {
      const cents = parseAmountToCents(amount)
      if (cents === null || cents <= 0) throw new Error('Geef een bedrag.')
      const dayOfMonth = Math.min(31, Math.max(1, Number(day) || 1))
      const build = CADENCES.find((c) => c.key === cadence)!.build

      await createRule({
        spaceId,
        partyId: initial?.partyId ?? null,
        description: description.trim() || 'Vaste kost',
        category: initial?.category ?? null,
        // The month matters only for the yearly shape, and it must come from
        // somewhere real: without it, RRULE reads "every year on the 4th" as
        // "the 4th of every month". See the note on CADENCES.
        rrule: build(dayOfMonth, Number(today.slice(5, 7))),
        startsOn: today,
        // Null here is a choice, not an omission: the checkbox below is what
        // makes "loopt door" explicit rather than merely unfilled (§10).
        endsOn: endsOn || null,
        mode,
        amountCents: cents,
        iban: mode === 'fixed' ? (initial?.iban ?? null) : null,
        paymentReference: mode === 'fixed' ? (initial?.paymentReference ?? null) : null,
        collectionMethod: collection,
        userId,
      })
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Opslaan lukte niet.')
      setBusy(false)
    }
  }

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <button onClick={onCancel} className="text-muted px-2 py-2">
          Annuleren
        </button>
        <span className="font-medium">Vaste kost</span>
        <span className="w-16" />
      </header>

      <div className="flex-1 scroll-area px-4 py-4">
        <Field
          label="Wat is het?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Huur, elektriciteit, verzekering"
        />

        <Card className="mb-4">
          <p className="text-sm text-muted mb-2">Het bedrag</p>
          <Choice
            selected={mode === 'fixed'}
            onSelect={() => setMode('fixed')}
            title="Altijd hetzelfde"
            hint="Zoals huur. Je kan het meteen betalen als het verschijnt."
          />
          <Choice
            selected={mode === 'variable'}
            onSelect={() => setMode('variable')}
            title="Verschilt elke keer"
            hint="Zoals elektriciteit. We schatten het, maar betalen pas als de factuur er is."
          />
        </Card>

        <Field
          label={mode === 'fixed' ? 'Bedrag' : 'Ongeveer hoeveel?'}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder="0,00"
          hint={mode === 'variable' ? 'Dit is alleen een startpunt. Het past zich aan.' : undefined}
        />

        <label className="block mb-4">
          <span className="block text-sm text-muted mb-1.5">Hoe vaak?</span>
          <select
            value={cadence}
            onChange={(e) => setCadence(e.target.value)}
            className="w-full min-h-[3.25rem] px-4 rounded-xl bg-surface border border-line text-lg"
          >
            {CADENCES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <Field
          label="Op welke dag van de maand?"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          inputMode="numeric"
          hint="Valt die dag niet in een maand — de 31e in februari — dan wordt die maand overgeslagen."
        />

        <label className="block mb-4">
          <span className="block text-sm text-muted mb-1.5">Hoe wordt het betaald?</span>
          <select
            value={collection}
            onChange={(e) => setCollection(e.target.value as CollectionMethod)}
            className="w-full min-h-[3.25rem] px-4 rounded-xl bg-surface border border-line text-lg"
          >
            {(['manual', 'direct_debit', 'standing_order'] as CollectionMethod[]).map((m) => (
              <option key={m} value={m}>
                {COLLECTION_LABELS[m]}
              </option>
            ))}
          </select>
          <span className="block text-xs text-muted mt-1.5">
            Een domiciliëring wordt vanzelf geïnd. Die vragen we achteraf te
            bevestigen in plaats van je een betaalknop te geven.
          </span>
        </label>

        <Field
          label="Tot wanneer? (mag leeg blijven)"
          type="date"
          value={endsOn}
          onChange={(e) => setEndsOn(e.target.value)}
          hint="Leeg betekent: blijft doorlopen tot je hem stopt."
        />

        <ErrorNote>{error}</ErrorNote>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line">
        <Button size="large" disabled={busy} onClick={save}>
          {busy ? 'Bezig…' : 'Opslaan'}
        </Button>
      </div>
    </Overlay>
  )
}

function Choice({
  selected,
  onSelect,
  title,
  hint,
}: {
  selected: boolean
  onSelect: () => void
  title: string
  hint: string
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-xl mb-2 border transition-colors ${
        selected ? 'border-ink bg-ink/5' : 'border-line'
      }`}
    >
      <p className="font-medium">{title}</p>
      <p className="text-sm text-muted">{hint}</p>
    </button>
  )
}
